import { DiscountMultiCourseMatrix, Discount, DiscountMatrixContext } from '../../types/DiscountTypes'
import {
  fixMathRounding,
  getActivitiesWithDiscountId,
  getPriceFromCalculation,
  getPriceFromCalculations,
  getBasePriceFromCalculation
} from './CalculateDiscountHelpers'
import {
  CalculateDiscountLogic,
  Calculation,
  CalculationDiscount,
  CalculationUserInput,
  DiscountType
} from './CalculateDiscountTypes'

export class MultiCourseMatrixDiscountLogic implements CalculateDiscountLogic {
  type: DiscountType = 'multi_course_matrix'
  discountDefinition: Discount | DiscountMultiCourseMatrix

  constructor(discount: Discount) {
    this.discountDefinition = discount
  }

  chooseEligible(previousCalculations: Calculation[], userInput: CalculationUserInput) {
    const def = this.discountDefinition as DiscountMultiCourseMatrix
    if (def.studentsOnly && !userInput.isStudent) return []
    return getActivitiesWithDiscountId(previousCalculations, this.discountDefinition.id)
  }

  calculate(previousCalculations: Calculation[], userInput: CalculationUserInput) {
    const def = this.discountDefinition as DiscountMultiCourseMatrix
    const eligibleActivities = this.chooseEligible(previousCalculations, userInput)

    // group activities with the same group
    const grouppedActivities: Record<string, Calculation[]> = {}
    previousCalculations.forEach((calculation: Calculation) => {
      if (!eligibleActivities.includes(calculation.activity.id)) return
      const group = calculation.activity.group || calculation.activity.id
      grouppedActivities[group] = grouppedActivities[group] || []
      grouppedActivities[group].push(calculation)
    })

    // get price for each group; output: [ { price: 100, activityIds: [1, 2] }, { price: 200, activityIds: [3] } ]
    const grouppedByPriceAndGroup = Object.entries(grouppedActivities).map(([, calculations]) => ({
      price: getPriceFromCalculations(calculations as Calculation[]),
      activityIds: calculations.map((c) => c.activity.id)
    }))

    // sort activities using strategy; use b - a (not ternary) so equal prices return 0 and preserve original order
    let sorted = grouppedByPriceAndGroup.map((c) => c.activityIds)
    if (def.strategy === 'max_price')
      sorted = grouppedByPriceAndGroup.sort((a, b) => b.price - a.price).map((c) => c.activityIds)
    if (def.strategy === 'min_price')
      sorted = grouppedByPriceAndGroup.sort((a, b) => a.price - b.price).map((c) => c.activityIds)

    // Applies a single course tier's discount to every calculation in a price group
    const applyTierToGroup = (activityIds: string[], courseDiscount: number) => {
      previousCalculations.forEach((calculation) => {
        if (!activityIds.includes(calculation.activity.id)) return
        const activityPrice = getPriceFromCalculation(calculation)
        const basePrice = getBasePriceFromCalculation(calculation)
        const discountAmount =
          this.discountDefinition.calculationType === 'amount'
            ? courseDiscount
            : fixMathRounding(basePrice * (courseDiscount / 100))
        const multiCourseMatrixDiscount: CalculationDiscount = {
          type: this.type,
          name: this.discountDefinition.name,
          id: this.discountDefinition.id,
          input: activityPrice,
          discount: discountAmount,
          output: fixMathRounding(activityPrice - discountAmount),
          discountCalculationType: this.discountDefinition.calculationType,
          discountValue: courseDiscount,
          discountGroup: this.discountDefinition.discountGroup
        }
        calculation.discounts = calculation.discounts || []
        calculation.discounts.push(multiCourseMatrixDiscount)
        calculation.finalPrice = multiCourseMatrixDiscount.output
      })
    }

    if (def.matrixContext === DiscountMatrixContext.Period) {
      // Period mode: each period is counted independently.
      // Activities from different periods do not affect each other's tier slot.
      // Within each period, slot = period-rank (sorted by price) + prior enrolments in that period + 1.
      const basketActivityIds = new Set(previousCalculations.map((c) => c.activity.id))
      const priorCountByPeriod = countPriorMatrixSlotsByPeriod(userInput, basketActivityIds)
      const tierByCourse = new Map(def.courseDiscounts.map((cd) => [cd.course, cd.discount]))

      // Group sorted basket entries by period, preserving the global price-sort order within each period
      const sortedByPeriod: Record<string, string[][]> = {}
      sorted.forEach((activityIds) => {
        const rep = previousCalculations.find((c) => activityIds.includes(c.activity.id))
        if (!rep) return
        const period = rep.activity.periodId || rep.activity.period
        sortedByPeriod[period] = sortedByPeriod[period] || []
        sortedByPeriod[period].push(activityIds)
      })

      // Apply tiers independently per period
      Object.entries(sortedByPeriod).forEach(([period, periodSorted]) => {
        const priorInPeriod = priorCountByPeriod[period] ?? 0
        periodSorted.forEach((activityIds, periodRank) => {
          const effectiveSlot = periodRank + priorInPeriod + 1
          const tierDiscount = tierByCourse.get(effectiveSlot)
          if (tierDiscount !== undefined) applyTierToGroup(activityIds, tierDiscount)
        })
      })
    } else {
      // Enrollment mode (default): slot = basket rank + 1, no prior offset
      def.courseDiscounts.forEach((cd) => {
        const basketIndex = cd.course - 1
        if (basketIndex >= 0 && basketIndex < sorted.length) {
          applyTierToGroup(sorted[basketIndex], cd.discount)
        }
      })
    }

    // Cap post-processing: if maxTotalPrice is set, walk sorted groups and boost the discount on
    // any activity that would push the cumulative discounted total over the cap.
    // Activities beyond the cap are discounted to 0 (fully free).
    //
    // NOTE: the cap accumulates over the ENTIRE basket, regardless of matrixContext.
    // When matrixContext === Period, tier discounts are applied per-period independently (above),
    // but the cap runs over the global sorted list — making it a basket-wide ceiling.
    // This is intentional: one shared cap per registration, not one cap per period.
    if (def.maxTotalPrice !== undefined && def.maxTotalPrice > 0) {
      let accumulated = 0

      for (const activityIds of sorted) {
        const groupFinalPrice = activityIds.reduce((sum, id) => {
          const calc = previousCalculations.find((c) => c.activity.id === id)
          return sum + (calc ? calc.finalPrice : 0)
        }, 0)

        if (accumulated >= def.maxTotalPrice) {
          // All budget spent — discount each remaining activity to 0
          activityIds.forEach((id) => {
            const calc = previousCalculations.find((c) => c.activity.id === id)
            if (!calc || calc.finalPrice <= 0) return
            const capDiscount: CalculationDiscount = {
              type: this.type,
              name: this.discountDefinition.name,
              id: this.discountDefinition.id,
              input: calc.finalPrice,
              discount: calc.finalPrice,
              output: 0,
              discountCalculationType: 'amount',
              discountValue: calc.finalPrice,
              discountGroup: this.discountDefinition.discountGroup
            }
            calc.discounts.push(capDiscount)
            calc.finalPrice = 0
          })
        } else if (accumulated + groupFinalPrice > def.maxTotalPrice) {
          // This group partially exceeds the cap — split remaining budget equally across activities
          const remainingBudget = def.maxTotalPrice - accumulated
          const activitiesInGroup = activityIds.length
          const budgetPerActivity = Math.floor(remainingBudget / activitiesInGroup)

          activityIds.forEach((id, idx) => {
            const calc = previousCalculations.find((c) => c.activity.id === id)
            if (!calc || calc.finalPrice <= 0) return
            // Last activity absorbs rounding remainder so total hits cap exactly
            const targetPrice =
              idx === activityIds.length - 1
                ? remainingBudget - budgetPerActivity * (activitiesInGroup - 1)
                : budgetPerActivity
            const capDiscountAmount = fixMathRounding(calc.finalPrice - targetPrice)
            if (capDiscountAmount <= 0) return
            const capDiscount: CalculationDiscount = {
              type: this.type,
              name: this.discountDefinition.name,
              id: this.discountDefinition.id,
              input: calc.finalPrice,
              discount: capDiscountAmount,
              output: fixMathRounding(calc.finalPrice - capDiscountAmount),
              discountCalculationType: 'amount',
              discountValue: capDiscountAmount,
              discountGroup: this.discountDefinition.discountGroup
            }
            calc.discounts.push(capDiscount)
            calc.finalPrice = capDiscount.output
          })
          accumulated = def.maxTotalPrice
        } else {
          accumulated += groupFinalPrice
        }
      }
    }

    return previousCalculations
  }
}

/** Count distinct prior activities per period, excluding anything already in the current basket. */
export const countPriorMatrixSlotsByPeriod = (
  userInput: CalculationUserInput,
  basketActivityIds: Set<string>
): Record<string, number> => {
  const priorCountByPeriod: Record<string, number> = {}
  if (userInput.userEnrolledActivityPeriods !== undefined) {
    const seenActivityIds = new Set<string>()
    for (const { activityId, periodId } of userInput.userEnrolledActivityPeriods) {
      if (!periodId || seenActivityIds.has(activityId) || basketActivityIds.has(activityId)) continue
      seenActivityIds.add(activityId)
      priorCountByPeriod[periodId] = (priorCountByPeriod[periodId] || 0) + 1
    }
    return priorCountByPeriod
  }
  for (const pId of userInput.userEnrolledPeriodIds || []) {
    priorCountByPeriod[pId] = (priorCountByPeriod[pId] || 0) + 1
  }
  return priorCountByPeriod
}
